/**
 * APP桥接工具 - 用于与React Native APP通信
 */
import { message } from 'antd';
import { detectWebView } from './webview-detection';
import { createAppMessageListener } from './message-handler';
import { addAppMessageListener, removeAppMessageListener } from './message-handler';


/**
 * 检测是否在React Native WebView环境中
 * @returns {boolean} 是否在React Native WebView中
 */
export function isReactNativeWebView() {
  try {
    const webViewEnv = detectWebView();
    return webViewEnv.isReactNativeWebView || (typeof window !== 'undefined' && window.ReactNativeWebView !== undefined);
  } catch (e) {
    console.warn('检测React Native WebView环境时出错:', e);
    return false;
  }
}

/**
 * 获取React Native WebView环境检测结果
 * @returns {Object} WebView环境信息
 */
export function getWebViewEnvironment() {
  return detectWebView();
}

/**
 * 发送消息到React Native APP
 * @param {Object} message - 要发送的消息对象
 */
export function sendMessageToApp(message) {
  try {
    if (isReactNativeWebView()) {
      window.ReactNativeWebView.postMessage(JSON.stringify(message));
      return true;
    }
  } catch (e) {
    console.warn('发送消息到APP时出错:', e);
  }
  console.warn('不在React Native WebView环境中，无法发送消息到APP');
  return false;
}

/**
 * 发送日志信息到APP
 * @param {string} message - 日志消息
 */
export function sendLogToApp(message) {
  // return sendMessageToApp({
  //   type: 'WEBVIEW_LOG',
  //   message: message
  // });
}

/**
 * 发送用户登录信息到APP
 * @param {Object} authData - 用户认证数据
 * @param {string} authData.userId - 用户ID
 */
export function sendUserLoginToApp(authData) {
  const { userId = null } = authData || {};
  
  return sendMessageToApp({
    type: 'USER_LOGIN',
    payload: {
      userId,
    }
  });
}

/**
 * 请求订阅推送通知
 * @param {Object} userData - 用户数据
 * @param {string} userData.userId - 用户ID
 * @param {string} userData.sessionCode - 会话码
 */
export function requestPushNotificationSubscription(userData) {
  const { userId, sessionCode } = userData;
  
  return sendMessageToApp({
    type: 'SUBSCRIBE_PUSH',
    payload: {
      userId,
      sessionCode
    }
  });
}

/**
 * 监听来自APP的消息
 * @param {Function} callback - 回调函数
 */
export function listenToAppMessages(callback) {
  if (isReactNativeWebView()) {
    window.addEventListener('message', (event) => {
      try {
        const data = JSON.parse(event.data);
        callback(data);
      } catch (e) {
        console.warn('解析来自APP的消息失败:', e);
      }
    });
  }
}

/**
 * 存储数据到APP
 * @param {string} key - 键名
 * @param {string} value - 键值
 */
export async function setAppStorage(key, value) {
  if (isReactNativeWebView()) {
    return new Promise((resolve, reject) => {
      const messageId = Date.now();
      
      // 创建专门用于处理此请求的监听器
      const messageListener = createAppMessageListener(messageId, resolve, reject, 'STORAGE_RESPONSE');
      addAppMessageListener(messageListener);
      
      // 发送请求
      sendMessageToApp({
        type: 'STORAGE_REQUEST',
        action: 'SET_ITEM',
        key,
        value: typeof value === 'string' ? value : JSON.stringify(value),
        messageId
      });
      
      // 设置超时
      setTimeout(() => {
        removeAppMessageListener(messageListener);
        reject(new Error('存储请求超时'));
      }, 5000);
    });
  } else {
    // 在浏览器环境中使用localStorage
    localStorage.setItem(key, typeof value === 'string' ? value : JSON.stringify(value));
    return Promise.resolve();
  }
}

/**
 * 从APP获取存储的数据
 * @param {string} key - 键名
 */
export async function getAppStorage(key) {
  if (isReactNativeWebView()) {
    return new Promise((resolve, reject) => {
      const messageId = Date.now();
      
      // 创建专门用于处理此请求的监听器
      const messageListener = createAppMessageListener(messageId, resolve, reject, 'STORAGE_RESPONSE');
      addAppMessageListener(messageListener);
      
      // 发送请求
      sendMessageToApp({
        type: 'STORAGE_REQUEST',
        action: 'GET_ITEM',
        key,
        messageId
      });
      
      // 设置超时
      setTimeout(() => {
        removeAppMessageListener(messageListener);
        reject(new Error('获取存储数据超时'));
      }, 5000);
    });
  } else {
    // 在浏览器环境中使用localStorage
    const value = localStorage.getItem(key);
    return Promise.resolve(value);
  }
}

/**
 * 打开外部链接
 * @param {string} url - 要打开的URL
 * @param {string} confirmMessage - 确认消息
 */
export async function openUrlExternally(url, confirmMessage = '确认要打开此链接吗？') {
  if (!url) {
    console.warn('URL为空，无法打开外部链接');
    return;
  }

  // 在APP环境中直接打开链接
  if (isReactNativeWebView()) {
    try {
      // 发送消息给APP要求打开外部链接
      const message = {
        type: 'OPEN_EXTERNAL_LINK',
        payload: { url, confirmMessage }
      };
      sendMessageToApp(message);
    } catch (e) {
      console.warn('发送打开外部链接请求失败:', e);
    }
  } else {
    // 在浏览器环境中，弹出确认框后打开新窗口
    if (confirmMessage !== undefined && window.confirm(confirmMessage)) {
      window.open(url, '_blank');
    }
  }
}